import { useQuery } from '@tanstack/react-query';
import { getCatBundleClient } from '@util/firestore/categories/categories.service';
import { Bundle, CategoryDocument } from 'models/category';
import { useMemo } from 'react';

export interface CategorySearchResult {
  category: CategoryDocument;
  parentPath: string;
  highlight: { before: string; match: string; after: string };
}

const MAX_RESULTS = 20;

const POPULAR_PATHS = [
  { label: 'Helmets', path: ['Helmets & Accessories', 'Helmets'] },
  { label: 'Gear combos', path: ['Gear', 'Gear Combos'] },
  { label: 'Boots', path: ['Boots & Accessories', 'Boots'] },
  { label: 'Goggles', path: ['Goggles & Accessories', 'Goggles'] },
  { label: 'Tires', path: ['Wheels, Tires & Parts', 'Tires'] },
  { label: 'Brake pads', path: ['Brakes', 'Brake Pads'] },
  { label: 'Slip-on exhaust', path: ['Exhaust', '4-Stroke Slip-On'] },
  { label: 'Air filters', path: ['Intake', 'Air Filters'] },
  { label: 'Shirts', path: ['Casual', 'Shirts'] },
  { label: 'Hats', path: ['Casual', 'Hats & Beanies'] },
  { label: 'Jackets', path: ['Casual', 'Jackets & Sweaters'] },
  { label: 'Shoes', path: ['Casual', 'Shoes'] },
];

export function getParentId(id: string) {
  return id.split('/').slice(0, -2).join('/');
}

export function flattenBundle(bundle: Bundle) {
  return [
    ...bundle['categories_new-query'],
    ...bundle['category1_new-query'],
    ...bundle['category2_new-query'],
    ...bundle['category3_new-query'],
    ...(bundle['category4_new-query'] ?? []),
  ].filter((c) => !c.is_seo_only);
}

/**
 * Whether a sport → category path is specific enough to list under: two
 * levels below the sport, except vehicles ("Bikes") and Casual, which stop at
 * the first level, and Collectibles, which needs only the sport.
 */
export function canListInCategory(categoryInfo: CategoryDocument[]) {
  return (
    categoryInfo[1]?.name === 'Bikes' ||
    (categoryInfo[0]?.name === 'Casual' && categoryInfo.length > 1) ||
    categoryInfo[0]?.name === 'Collectibles' ||
    categoryInfo.length > 2
  );
}

function splitHighlight(name: string, tokens: string[]) {
  const lower = name.toLowerCase();
  const phrase = tokens.join(' ');
  const term = lower.includes(phrase)
    ? phrase
    : tokens.find((t) => lower.includes(t));
  if (!term) return { before: name, match: '', after: '' };
  const at = lower.indexOf(term);
  return {
    before: name.slice(0, at),
    match: name.slice(at, at + term.length),
    after: name.slice(at + term.length),
  };
}

/**
 * Builds the category tree below `rootId` (a sport) from the static category
 * bundle, so browsing and searching run locally without network round trips.
 */
export function buildCategoryTree(all: CategoryDocument[], rootId?: string) {
  const byId = new Map<string, CategoryDocument>();
  const childrenOf = new Map<string, CategoryDocument[]>();

  if (rootId) {
    all
      .filter((c) => c.id === rootId || c.id.startsWith(`${rootId}/`))
      .forEach((c) => byId.set(c.id, c));
    byId.forEach((c) => {
      if (c.id === rootId) return;
      const parentId = getParentId(c.id);
      if (!byId.has(parentId)) return;
      const siblings = childrenOf.get(parentId) ?? [];
      siblings.push(c);
      childrenOf.set(parentId, siblings);
    });
    childrenOf.forEach((list) =>
      list.sort((a, b) => a.name.localeCompare(b.name))
    );
  }

  const getChildren = (id: string) => childrenOf.get(id) ?? [];

  const getChain = (id: string) => {
    const chain: CategoryDocument[] = [];
    let current = byId.get(id);
    while (current) {
      chain.unshift(current);
      if (current.id === rootId) break;
      current = byId.get(getParentId(current.id));
    }
    return chain[0]?.id === rootId ? chain : [];
  };

  const isSelectable = (c: CategoryDocument) =>
    canListInCategory(getChain(c.id));

  const pathLabel = (chain: CategoryDocument[]) =>
    chain.map((c) => c.name).join(' › ');

  const reachable = [...byId.values()].filter(
    (c) => c.id !== rootId && getChain(c.id).length > 0
  );
  const searchable = reachable.filter(isSelectable).map((c) => {
    const chain = getChain(c.id);
    return {
      category: c,
      chain,
      haystack: chain.map((x) => x.name.toLowerCase()).join(' '),
    };
  });

  const search = (query: string): CategorySearchResult[] => {
    const tokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return [];
    const phrase = tokens.join(' ');
    return searchable
      .filter((s) => tokens.every((t) => s.haystack.includes(t)))
      .map((s) => {
        const name = s.category.name.toLowerCase();
        const rank = name.startsWith(phrase)
          ? 0
          : name.includes(phrase)
          ? 1
          : tokens.some((t) => name.includes(t))
          ? 2
          : 3;
        return { ...s, rank };
      })
      .sort(
        (a, b) =>
          a.rank - b.rank ||
          a.category.cat_level - b.category.cat_level ||
          a.category.name.localeCompare(b.category.name)
      )
      .slice(0, MAX_RESULTS)
      .map((s) => ({
        category: s.category,
        parentPath: pathLabel(s.chain.slice(1, -1)),
        highlight: splitHighlight(s.category.name, tokens),
      }));
  };

  const popular = POPULAR_PATHS.flatMap(({ label, path }) => {
    const match = searchable.find((s) =>
      path.every(
        (name, i) => s.chain[s.chain.length - path.length + i]?.name === name
      )
    );
    return match ? [{ label, category: match.category }] : [];
  });

  return {
    byId,
    getChildren,
    getChain,
    isSelectable,
    pathLabel,
    search,
    popular,
  };
}

export function useCategoryTree(rootId?: string) {
  const { data: bundle, isLoading } = useQuery({
    queryKey: ['categories-bundle'],
    queryFn: () => getCatBundleClient(),
    staleTime: Infinity,
  });

  const all = useMemo(() => (bundle ? flattenBundle(bundle) : []), [bundle]);
  const tree = useMemo(() => buildCategoryTree(all, rootId), [all, rootId]);

  return { ...tree, isLoading };
}
